"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, Search } from "lucide-react";
import AnswerBox from "@/components/shared/AnswerBox";
import EmptyState from "@/components/unis/EmptyState";
import UniCTABanner from "@/components/unis/UniCTABanner";
import SearchFilters from "@/components/unis/SearchFilters";
import UniversityCard from "@/components/unis/UniversityCard";
import UniversityCardSkeleton from "@/components/unis/UniversityCardSkeleton";
import {
  EMPTY_FILTERS,
  filterUniversities,
  universities,
  type UniFilters,
} from "@/lib/universities";

const PAGE_SIZE = 8;
const SKELETON_COUNT = 4;

export default function UnisPageContent() {
  const [filters, setFilters] = useState<UniFilters>(EMPTY_FILTERS);
  const [searchDraft, setSearchDraft] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const firstRender = useRef(true);

  // Suche entprellt in die Filter übernehmen
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchDraft ? prev : { ...prev, search: searchDraft }
      );
    }, 250);
    return () => clearTimeout(timer);
  }, [searchDraft]);

  // Bei Filterwechsel kurz Skeletons zeigen und Paginierung zurücksetzen
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setLoading(true);
    setVisibleCount(PAGE_SIZE);
    const timer = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(timer);
  }, [filters]);

  const results = useMemo(() => filterUniversities(universities, filters), [filters]);
  const visible = results.slice(0, visibleCount);
  const remaining = results.length - visible.length;

  const staatlichCount = universities.filter((u) => u.type === "Staatlich").length;
  const privatCount = universities.length - staatlichCount;

  function resetFilters() {
    setSearchDraft("");
    setFilters(EMPTY_FILTERS);
  }

  function scrollToList() {
    document.getElementById("unis-liste")?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <>
      {/* Hero */}
      <section className="relative bg-paper overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 55% at 50% 0%, oklch(94.5% 0.026 268 / 0.9) 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
          {/* CSS entrance (.enter): visible from the first paint (LCP) */}
          <div className="flex flex-col items-center text-center gap-6">
            <p className="enter flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-accent">
              <span className="w-1.5 h-1.5 bg-accent shrink-0" aria-hidden="true" />
              Finde die perfekte Uni
            </p>
            <h1 className="enter [--enter-delay:90ms] font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-ink">
              Univergleich <span className="text-accent">Deutschland</span>
            </h1>
            <p className="enter [--enter-delay:180ms] text-lg text-ink-soft leading-relaxed max-w-2xl">
              Vergleiche staatliche und private Hochschulen, Studiengänge und
              Standorte in Deutschland – und finde die beste Uni für dich.
            </p>

            {/* Hero-Suche */}
            <div className="enter [--enter-delay:270ms] w-full max-w-xl">
              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Universität oder Stadt suchen …"
                  aria-label="Universität oder Stadt suchen"
                  className="w-full rounded-full bg-white pl-12 pr-5 py-4 text-base text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-accent/40 transition-shadow"
                />
              </div>
            </div>

            <div className="enter [--enter-delay:360ms] flex flex-col items-center gap-4">
              <button
                onClick={scrollToList}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-accent text-paper text-base font-semibold min-h-[52px] hover:bg-accent-deep transition-colors duration-200 cursor-pointer"
              >
                Unis entdecken
                <ArrowDown size={16} aria-hidden="true" />
              </button>
              <p className="text-sm text-ink-faint">
                <span className="font-bold text-ink tnum">{staatlichCount}</span> staatliche ·{" "}
                <span className="font-bold text-ink tnum">{privatCount}</span> private
                Hochschulen
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AEO-Kurzantwort direkt unter dem Hero */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <AnswerBox
          question="An welchen Unis kann man in Deutschland Medizin studieren?"
          answer={`Auf dieser Seite vergleichst du ${universities.length} Hochschulen in Deutschland, an denen du Medizin studieren kannst – ${staatlichCount} staatliche und ${privatCount} private. Für jede Uni findest du Standort, Studiengänge, Studiengebühren und die Auswahlkriterien im Überblick.`}
          keyTakeaways={[
            `${universities.length} Hochschulen im direkten Vergleich – nach Stadt, Bundesland, Trägerschaft und Studiengang filterbar.`,
            "Staatliche Unis vergeben über hochschulstart.de (ABQ, AdH, ZEQ); private Hochschulen nutzen eigene Auswahlverfahren.",
            "Jede Detailseite zeigt die konkreten Zulassungskriterien der jeweiligen Hochschule.",
          ]}
          audience="Für Studieninteressierte, die den passenden Studienort für Human-, Zahn-, Tier- oder Veterinärmedizin in Deutschland suchen."
        />
      </div>

      {/* Liste + Filter */}
      <section id="unis-liste" className="bg-paper border-t border-line scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-8">
            <SearchFilters
              filters={filters}
              searchDraft={searchDraft}
              onSearchChange={setSearchDraft}
              onChange={setFilters}
              onReset={resetFilters}
              resultCount={results.length}
            />

            <div className="lg:col-span-8 xl:col-span-9">
              {/* Ergebniszähler (Desktop – mobil sitzt er in der Sticky-Leiste) */}
              <p
                className="hidden lg:block text-sm text-ink-soft mb-6"
                aria-live="polite"
              >
                <span className="font-bold text-ink tnum">{results.length}</span>{" "}
                {results.length === 1 ? "Universität" : "Universitäten"} gefunden
              </p>

              <div className="grid md:grid-cols-2 gap-5 lg:gap-6 items-stretch mt-6 lg:mt-0">
                {loading ? (
                  Array.from({ length: SKELETON_COUNT }, (_, i) => (
                    <UniversityCardSkeleton key={i} />
                  ))
                ) : visible.length === 0 ? (
                  <EmptyState onReset={resetFilters} />
                ) : (
                  visible.map((university, i) => (
                    <UniversityCard
                      key={university.id}
                      university={university}
                      index={i}
                    />
                  ))
                )}
              </div>

              {/* Mehr laden */}
              {!loading && remaining > 0 && (
                <div className="flex flex-col items-center gap-3 mt-10">
                  <button
                    onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-line bg-white text-ink text-sm font-semibold hover:border-accent hover:text-accent transition-colors duration-200 cursor-pointer min-h-[48px]"
                  >
                    Weitere Unis laden
                    <ArrowDown size={15} aria-hidden="true" />
                  </button>
                  <p className="text-xs text-ink-faint tnum">
                    {visible.length} von {results.length} angezeigt
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <UniCTABanner />
    </>
  );
}
